/**
 * Alle verfuegbaren Feature-Keys die pro Tenant aktiviert/deaktiviert werden koennen.
 */
export const TenantFeatureKeys = {
  Zeiterfassung: 'zeiterfassung',
  Baustellentagebuch: 'baustellentagebuch',
  Arbeitsbericht: 'arbeitsbericht',
  Maengel: 'maengel',
  Nachtraege: 'nachtraege',
  WhatsApp: 'whatsapp',
  FelixChat: 'felixChat',
  Zeitplan: 'zeitplan',
  Gantt: 'gantt',
  /** KI-Projektberichte / Berichte-Hub (technischer Key, API `projektberichte`). */
  Projektberichte: 'projektberichte',
  Aufgaben: 'aufgaben',
  /** Arbeitsbereiche-Hub (Werkstatt, Halle, Buero) – Enterprise-only. */
  Arbeitsbereiche: 'arbeitsbereiche',
  /** Felix E-Mail-Analyse (Postfaecher verbinden, automatische KI-Auswertung). */
  EmailAnalyse: 'emailAnalyse',
  /** Felix-Sammel-Mailbox (dedizierte CC-Adresse, Graph-Ingest). Addon – vom SystemAdmin aktivierbar. */
  FelixSammelMailbox: 'felixSammelMailbox',
} as const;

export type TenantFeatureKey = typeof TenantFeatureKeys[keyof typeof TenantFeatureKeys];

/**
 * Feature-Map: Key -> boolean (aktiv/inaktiv).
 */
export type TenantFeatures = Record<string, boolean>;

/**
 * Default-Features fuer Fallback (wenn API nicht erreichbar).
 */
export const DEFAULT_TENANT_FEATURES: TenantFeatures = {
  [TenantFeatureKeys.Zeiterfassung]: true,
  [TenantFeatureKeys.Baustellentagebuch]: true,
  [TenantFeatureKeys.Arbeitsbericht]: true,
  [TenantFeatureKeys.Maengel]: true,
  [TenantFeatureKeys.Nachtraege]: true,
  [TenantFeatureKeys.WhatsApp]: true,
  [TenantFeatureKeys.FelixChat]: true,
  [TenantFeatureKeys.Zeitplan]: false,
  [TenantFeatureKeys.Gantt]: false,
  [TenantFeatureKeys.Projektberichte]: true,
  [TenantFeatureKeys.Aufgaben]: true,
  [TenantFeatureKeys.Arbeitsbereiche]: false,
  [TenantFeatureKeys.EmailAnalyse]: false,
  [TenantFeatureKeys.FelixSammelMailbox]: false,
};

/**
 * Feature-Definitionen mit Anzeigenamen fuer das Admin-UI.
 */
export interface FeatureDefinition {
  key: TenantFeatureKey;
  name: string;
  beschreibung: string;
  /** Ob das Feature im Admin-UI (Funktionen-Tab) angezeigt wird. Default: true. */
  sichtbar?: boolean;
}

export const FEATURE_DEFINITIONEN: FeatureDefinition[] = [
  {
    key: TenantFeatureKeys.Projektberichte,
    name: 'Berichte (KI)',
    beschreibung: 'KI-Auswertungen Tag/Woche/Monat/Gesamt und Berichte-Hub (techn. Key „projektberichte“)',
  },
  { key: TenantFeatureKeys.Baustellentagebuch, name: 'Baustellentagebuch', beschreibung: 'Offizielles Baustellentagebuch mit Berichten' },
  { key: TenantFeatureKeys.Zeiterfassung, name: 'Zeiterfassung', beschreibung: 'Stundenerfassung pro Mitarbeiter und Projekt' },
  { key: TenantFeatureKeys.Arbeitsbericht, name: 'Tagesberichte (Feld)', beschreibung: 'Tägliche Feldberichte via WhatsApp und Portal – Bautagesberichte für Mitarbeiter' },
  { key: TenantFeatureKeys.Aufgaben, name: 'Aufgabenverwaltung', beschreibung: 'Tasks und Aufgaben zuweisen und verfolgen' },
  { key: TenantFeatureKeys.Maengel, name: 'Mängelverwaltung', beschreibung: 'Mängel erfassen, dokumentieren und verfolgen' },
  { key: TenantFeatureKeys.Nachtraege, name: 'Nachtragsverwaltung', beschreibung: 'Nachträge erstellen und genehmigen' },
  { key: TenantFeatureKeys.WhatsApp, name: 'WhatsApp-Integration', beschreibung: 'Baustellenberichte via WhatsApp empfangen' },
  { key: TenantFeatureKeys.FelixChat, name: 'Felix KI-Assistent', beschreibung: 'KI-Chatbot im Portal für Hilfe und Aktionen', sichtbar: false },
  { key: TenantFeatureKeys.Zeitplan, name: 'Zeitplanung', beschreibung: 'Gantt-basierte Produktionsplanung (Coming Soon)' },
  { key: TenantFeatureKeys.Gantt, name: 'Gantt-Ansicht', beschreibung: 'Visuelle Gantt-Diagramme (Coming Soon)' },
  { key: TenantFeatureKeys.Arbeitsbereiche, name: 'Arbeitsbereiche', beschreibung: 'Werkstatt- und Standort-Kontexte neben Projekten (Enterprise)' },
  { key: TenantFeatureKeys.EmailAnalyse, name: 'E-Mail-Analyse', beschreibung: 'Felix analysiert eingehende E-Mails automatisch (KI-gestützt)' },
];

/**
 * Konfiguration fuer den `kannNutzen()`-Check im AuthService
 * und die `*appKannNutzen` Direktive.
 *
 * Alle gesetzten Bedingungen muessen gleichzeitig erfuellt sein (AND-Logik).
 * Nicht gesetzte Felder werden ignoriert.
 */
export interface KannNutzenConfig {
  /** Feature-Key der geprueft werden soll (muss aktiv sein). */
  feature?: TenantFeatureKey;
  /** Mindestens eine dieser Rollen muss vorhanden sein (OR-Logik). */
  rollen?: string[];
  /** Bereichs-Berechtigung die geprueft werden soll. */
  berechtigung?: {
    bereich: string;
    aktion: 'lesen' | 'bearbeiten';
  };
}
